import { EKO_API_BASE_URL, PORTAL_API_BASE_URL, PORTAL_DEVICE_NUMBER } from '@/portal/config';

// No key in source: set VITE_API_KEY in .env.local (dev) or .env.production.local (deploy build).
const apiKey = (): string => (import.meta.env.VITE_API_KEY as string | undefined) || '';

export class PortalApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'PortalApiError';
    this.status = status;
  }
}

export interface PortalRequestInit {
  method?: 'GET' | 'POST';
  body?: unknown;
  token?: string | null;
  accept?: string;
}

const send = (baseUrl: string, path: string, init: PortalRequestInit, extraHeaders: Record<string, string>): Promise<Response> => {
  const headers: Record<string, string> = {
    'x-device-number': PORTAL_DEVICE_NUMBER,
    Accept: init.accept ?? 'application/json',
    ...extraHeaders,
  };

  if (init.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }

  if (init.token) {
    headers.Authorization = `Bearer ${init.token}`;
  }

  return fetch(`${baseUrl}${path}`, {
    method: init.method ?? 'GET',
    headers,
    body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
  });
};

const readJson = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    throw new PortalApiError(response.status, `HTTP ${response.status}`);
  }
  return (await response.json()) as T;
};

/** A call to the Customer API (key + device number + the login token). */
export const portalFetch = (path: string, init: PortalRequestInit = {}): Promise<Response> =>
  send(PORTAL_API_BASE_URL, path, init, { 'X-API-Key': apiKey() });

export const portalJson = async <T>(path: string, init: PortalRequestInit = {}): Promise<T> =>
  readJson<T>(await portalFetch(path, init));

/** A call to the lab (EKO) API; used by the support chat only, with the EKO token. */
export const ekoFetch = (path: string, init: PortalRequestInit = {}): Promise<Response> =>
  send(EKO_API_BASE_URL, path, init, {});

export const ekoJson = async <T>(path: string, init: PortalRequestInit = {}): Promise<T> =>
  readJson<T>(await ekoFetch(path, init));
